import React, { useState, useEffect, useRef } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { Plus, Minus, Navigation, MapPin, Clock, Route as RouteIcon, Save } from 'lucide-react';
import { saasApiService } from '../../../services/saasApi';

/// <reference types="@types/google.maps" />

interface Waypoint {
  id: string;
  name: string;
  address: string;
  type: 'origin' | 'waypoint';
}

interface OptimizedRoute {
  optimized_waypoints: any[];
  total_distance: number;
  total_duration: number;
  maps_link: string;
}

const MapRoutePlanner: React.FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const directionsServiceRef = useRef<google.maps.DirectionsService | null>(null);
  const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  
  // Store autocomplete instances to avoid re-initialization
  const autocompleteRefs = useRef<Map<string, google.maps.places.Autocomplete>>(new Map());
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  
  const [waypoints, setWaypoints] = useState<Waypoint[]>([
    { id: '1', name: 'Origen', address: 'Vega Pacasmayo, San Martín de Porres 15112, Perú', type: 'origin' }
  ]);
  
  const [optimizedRoute, setOptimizedRoute] = useState<OptimizedRoute | null>(null);
  const [loading, setLoading] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isMapClickMode, setIsMapClickMode] = useState(false);
  
  // Form states
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [routeName, setRouteName] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [isRouteActive, setIsRouteActive] = useState(true);
  const [notes, setNotes] = useState('');
  
  // Data for dropdowns
  const [clients, setClients] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    const initMap = async () => {
      try {
        const loader = new Loader({
          apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
          version: 'weekly',
          libraries: ['places', 'geometry']
        });

        await loader.load();

        if (mapRef.current) {
          const map = new google.maps.Map(mapRef.current, {
            zoom: 13,
            center: { lat: -12.0464, lng: -77.0428 }, // Lima, Peru
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
          });

          mapInstanceRef.current = map;
          directionsServiceRef.current = new google.maps.DirectionsService();
          directionsRendererRef.current = new google.maps.DirectionsRenderer({
            draggable: true,
            map: map,
          });

          // Initialize geocoder for reverse geocoding
          geocoderRef.current = new google.maps.Geocoder();

          setMapLoaded(true);
          
          // Don't initialize autocomplete here - let useEffect handle it
        }
      } catch (error) {
        console.error('Error loading Google Maps:', error);
      }
    };

    initMap();
    loadFormData();
  }, []);

  // Add map click listener when isMapClickMode changes
  useEffect(() => {
    if (mapInstanceRef.current && mapLoaded) {
      // Remove existing listeners
      google.maps.event.clearListeners(mapInstanceRef.current, 'click');
      
      // Add new listener with current isMapClickMode state
      mapInstanceRef.current.addListener('click', (event: google.maps.MapMouseEvent) => {
        console.log('Map clicked, isMapClickMode:', isMapClickMode, 'event.latLng:', event.latLng);
        if (isMapClickMode && event.latLng) {
          handleMapClick(event.latLng);
        }
      });
    }
  }, [isMapClickMode, mapLoaded]);

  // Initialize Google Places Autocomplete - now unused, handled in useEffect
  const initializeAutocomplete = () => {
    // This function is no longer used - autocomplete is handled in useEffect
  };

  const loadFormData = async () => {
    try {
      const [clientsRes, driversRes, vehiclesRes] = await Promise.all([
        saasApiService.getClients(),
        saasApiService.getDrivers(),
        saasApiService.getVehicles()
      ]);
      
      setClients(clientsRes);
      setDrivers(driversRes);
      setVehicles(vehiclesRes);
    } catch (error) {
      console.error('Error loading form data:', error);
    }
  };

  const addWaypoint = () => {
    const newWaypoint: Waypoint = {
      id: Date.now().toString(),
      name: `Parada ${waypoints.length}`,
      address: '',
      type: 'waypoint'
    };
    setWaypoints([...waypoints, newWaypoint]);
  };

  const removeWaypoint = (id: string) => {
    if (waypoints.length <= 1) return;
    
    // Remove autocomplete instance for this waypoint
    const autocomplete = autocompleteRefs.current.get(id);
    if (autocomplete) {
      google.maps.event.clearInstanceListeners(autocomplete);
      autocompleteRefs.current.delete(id);
    }
    
    setWaypoints(waypoints.filter(wp => wp.id !== id));
  };

  const updateWaypoint = (id: string, field: keyof Waypoint, value: string) => {
    setWaypoints(waypoints.map(wp => 
      wp.id === id ? { ...wp, [field]: value } : wp
    ));
  };

  const handleMapClick = async (latLng: google.maps.LatLng) => {
    console.log('handleMapClick called with:', latLng);
    if (!geocoderRef.current) {
      console.log('No geocoder available');
      return;
    }

    try {
      console.log('Starting geocoding...');
      const response = await new Promise<google.maps.GeocoderResponse>((resolve, reject) => {
        geocoderRef.current!.geocode(
          { location: latLng },
          (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
            console.log('Geocoding response:', status, results);
            if (status === 'OK' && results) {
              resolve({ results } as google.maps.GeocoderResponse);
            } else {
              reject(new Error(`Geocoding failed: ${status}`));
            }
          }
        );
      });

      if (response.results && response.results.length > 0) {
        const address = response.results[0].formatted_address;
        console.log('Got address:', address);
        
        // Add new waypoint with the clicked location
        const newWaypoint: Waypoint = {
          id: Date.now().toString(),
          name: `Parada ${waypoints.length}`,
          address: address,
          type: 'waypoint'
        };
        
        console.log('Adding waypoint:', newWaypoint);
        setWaypoints(prev => [...prev, newWaypoint]);
        setIsMapClickMode(false); // Exit click mode after adding waypoint
      }
    } catch (error) {
      console.error('Error getting address from coordinates:', error);
      alert('Error al obtener la dirección del punto seleccionado');
    }
  };

  useEffect(() => {
    if (mapLoaded && waypoints.length > 0) {
      // Clear existing autocomplete instances for removed waypoints
      const currentWaypointIds = new Set(waypoints.map(wp => wp.id));
      Array.from(autocompleteRefs.current.entries()).forEach(([id, autocomplete]) => {
        if (!currentWaypointIds.has(id)) {
          google.maps.event.clearInstanceListeners(autocomplete);
          autocompleteRefs.current.delete(id);
        }
      });
      
      // Only initialize autocomplete for waypoints that don't have it yet
      setTimeout(() => {
        waypoints.forEach((waypoint) => {
          if (!autocompleteRefs.current.has(waypoint.id)) {
            const input = document.getElementById(`address-${waypoint.id}`) as HTMLInputElement;
            if (input && window.google && window.google.maps && window.google.maps.places && window.google.maps.places.Autocomplete) {
              try {
                const autocomplete = new google.maps.places.Autocomplete(input, {
                  types: ['establishment', 'geocode'],
                  componentRestrictions: { country: 'pe' },
                });

                autocompleteRefs.current.set(waypoint.id, autocomplete);

                autocomplete.addListener('place_changed', () => {
                  const place = autocomplete.getPlace();
                  if (place.formatted_address) {
                    updateWaypoint(waypoint.id, 'address', place.formatted_address);
                  }
                });
              } catch (error) {
                console.error('Error initializing autocomplete for waypoint:', waypoint.id, error);
              }
            }
          }
        });
      }, 100);
    }
  }, [waypoints.length, mapLoaded]);

  const calculateRoute = async () => {
    if (!mapLoaded || !directionsServiceRef.current || !directionsRendererRef.current) {
      alert('El mapa aún se está cargando. Intenta de nuevo en un momento.');
      return;
    }

    const validWaypoints = waypoints.filter(wp => wp.address.trim() !== '');
    
    if (validWaypoints.length < 2) {
      alert('Se requieren al menos 2 paradas (origen + 1 parada mínimo)');
      return;
    }

    setLoading(true);

    try {
      const origin = validWaypoints[0].address;
      const destination = validWaypoints[validWaypoints.length - 1].address;
      const waypointsForRoute = validWaypoints.slice(1, -1).map(wp => ({
        location: wp.address,
        stopover: true
      }));

      const request: google.maps.DirectionsRequest = {
        origin: origin,
        destination: destination,
        waypoints: waypointsForRoute,
        optimizeWaypoints: true,
        travelMode: google.maps.TravelMode.DRIVING,
        unitSystem: google.maps.UnitSystem.METRIC,
        avoidHighways: false,
        avoidTolls: false
      };

      directionsServiceRef.current.route(request, (result: google.maps.DirectionsResult | null, status: google.maps.DirectionsStatus) => {
        if (status === 'OK' && result) {
          directionsRendererRef.current?.setDirections(result);
          
          // Calculate total distance and duration
          let totalDistance = 0;
          let totalDuration = 0;
          
          result.routes[0].legs.forEach((leg: google.maps.DirectionsLeg) => {
            if (leg.distance) totalDistance += leg.distance.value;
            if (leg.duration) totalDuration += leg.duration.value;
          });

          const optimizedData: OptimizedRoute = {
            optimized_waypoints: validWaypoints,
            total_distance: Math.round(totalDistance / 1000), // Convert to km
            total_duration: Math.round(totalDuration / 60), // Convert to minutes
            maps_link: `https://www.google.com/maps/dir/${validWaypoints.map(wp => encodeURIComponent(wp.address)).join('/')}`
          };

          setOptimizedRoute(optimizedData);
          setLoading(false);
        } else {
          console.error('Directions request failed due to ' + status);
          alert('Error al calcular la ruta: ' + status);
          setLoading(false);
        }
      });

    } catch (error) {
      console.error('Error calculating route:', error);
      alert('Error al calcular la ruta');
      setLoading(false);
    }
  };

  const saveRoute = async () => {
    if (!optimizedRoute) {
      alert('Primero calcula una ruta antes de guardarla');
      return;
    }

    if (!routeName.trim()) {
      alert('Por favor ingresa un nombre para la ruta');
      return;
    }

    try {
      setLoading(true);
      
      const routeData = {
        route_name: routeName,
        client_id: selectedClient ? parseInt(selectedClient) : null,
        waypoints: waypoints.map(wp => wp.address),
        status: isRouteActive ? 'scheduled' : 'inactive',
        notes: notes,
        route_plan_data: {
          waypoints: waypoints,
          optimized_route: optimizedRoute,
          total_distance: optimizedRoute.total_distance,
          total_duration: optimizedRoute.total_duration
        }
      };

      console.log('Guardando ruta con datos:', routeData);
      
      const response = await saasApiService.saveRoutePlan(routeData);
      console.log('Respuesta del servidor:', response);
      
      alert('Ruta guardada exitosamente. Ve a "Lista de Rutas" para verla.');
      setShowSaveForm(false);
      
      // Clear form
      setRouteName('');
      setSelectedClient('');
      setIsRouteActive(true);
      setNotes('');
      
      // Dispatch event to refresh route list
      window.dispatchEvent(new CustomEvent('routeSaved'));
    } catch (error: any) {
      console.error('Error guardando ruta:', error);
      alert(`Error al guardar la ruta: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-screen">
      {/* Left Panel - Route Planning */}
      <div className="lg:col-span-1 bg-white rounded-lg shadow-md p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <Navigation className="h-5 w-5 mr-2 text-blue-600" />
            Planificador de Rutas
          </h2>
        </div>

        {/* Map Click Mode Indicator */}
        {isMapClickMode && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex items-center text-blue-700">
              <MapPin className="h-4 w-4 mr-2" />
              <span className="text-sm font-medium">Modo selección activo</span>
            </div>
            <p className="text-xs text-blue-600 mt-1">
              Haz clic en cualquier punto del mapa para agregar una parada
            </p>
          </div>
        )}

        {/* Waypoints */}
        <div className="space-y-4 mb-6">
          <h3 className="font-medium text-gray-700">Puntos de Ruta</h3>
          {waypoints.map((waypoint, index) => (
            <div key={waypoint.id} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-2 ${
                    waypoint.type === 'origin' ? 'bg-green-500' : 'bg-blue-500'
                  }`}></div>
                  <span className="font-medium text-sm">
                    {waypoint.type === 'origin' ? 'Origen' : `Parada ${index}`}
                  </span>
                </div>
                {waypoint.type === 'waypoint' && (
                  <button
                    onClick={() => removeWaypoint(waypoint.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              <input
                id={`address-${waypoint.id}`}
                type="text"
                placeholder="Ingresa la dirección"
                value={waypoint.address}
                onChange={(e) => updateWaypoint(waypoint.id, 'address', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          ))}
          
          <div className="flex justify-between items-center mb-6">
            <div className="flex gap-2">
              <button
                onClick={addWaypoint}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar Parada
              </button>
              <button
                onClick={() => {
                  console.log('Toggle map click mode, current state:', isMapClickMode);
                  setIsMapClickMode(!isMapClickMode);
                }}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  isMapClickMode 
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <MapPin className="h-4 w-4 mr-2" />
                {isMapClickMode ? 'Cancelar Selección' : 'Seleccionar en Mapa'}
              </button>
            </div>
          </div>

          {/* Calculate Route Button */}
          <button
            onClick={calculateRoute}
            disabled={loading || !mapLoaded}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center mb-4"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <RouteIcon className="h-4 w-4 mr-2" />
            )}
            {loading ? 'Calculando...' : 'Calcular Ruta'}
          </button>

          {/* Route Summary */}
          {optimizedRoute && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-green-800 mb-2">Ruta Calculada</h4>
              <div className="space-y-2 text-sm text-green-700">
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2" />
                  Distancia: {optimizedRoute.total_distance} km
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2" />
                  Tiempo: {formatDuration(optimizedRoute.total_duration)}
                </div>
              </div>
              
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-4">
                  <a
                    href={optimizedRoute.maps_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm"
                  >
                    <Navigation className="h-4 w-4 mr-1" />
                    Abrir en Google Maps
                  </a>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(optimizedRoute.maps_link);
                        alert('¡Link copiado! Puedes compartirlo donde quieras.');
                      } catch (e) {
                        window.open(optimizedRoute.maps_link, '_blank');
                      }
                    }}
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Copiar link
                  </button>
                </div>
                
                <button
                  onClick={() => setShowSaveForm(true)}
                  className="block w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 text-sm"
                >
                  <Save className="h-4 w-4 mr-2 inline" />
                  Guardar Ruta
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Directions Panel */}
        <div id="directions-panel" className="text-sm"></div>
      </div>

      {/* Right Panel - Map */}
      <div className="lg:col-span-2 bg-white rounded-lg shadow-md overflow-hidden">
        <div 
          ref={mapRef}
          className="w-full h-full min-h-[600px]"
          style={{ minHeight: '600px' }}
        />
        {!mapLoaded && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Cargando mapa...</p>
            </div>
          </div>
        )}
      </div>

      {/* Save Route Modal */}
      {showSaveForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold mb-4">Guardar Ruta Planificada</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de la Ruta *
                </label>
                <input
                  type="text"
                  value={routeName}
                  onChange={(e) => setRouteName(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="Ej: Ruta Centro - Norte"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cliente *
                </label>
                <select
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="">Seleccionar cliente</option>
                  {clients.map((client: any) => (
                    <option key={client.id} value={client.id}>
                      {client.company_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado de la Ruta *
                </label>
                <select
                  value={isRouteActive ? 'active' : 'inactive'}
                  onChange={(e) => setIsRouteActive(e.target.value === 'active')}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="active">Ruta Activa</option>
                  <option value="inactive">Ruta Inactiva</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="Notas adicionales..."
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowSaveForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={saveRoute}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapRoutePlanner;
